// „Was ist neu?" am Hinweis „Neue Version verfügbar" (User 28.09.2026: „ein
// (i), und wenn man drauf drückt, ein Popup, das in Kurzfassung zeigt, was
// gemacht wurde").
//
// Quelle: `deploy/deploy.sh` schreibt vor dem Build `git log` in
// `apps/web/aenderungen.txt`; Vite liefert daraus `/aenderungen.json` aus und
// gibt der laufenden App ihre eigenen Commits als VITE_APP_COMMITS mit. Neu ist,
// was die ausgelieferte Liste hat und die laufende nicht. Über Commit-Kennungen
// statt über Datum, weil auf PROD die Squash-Commits von dev erst mit der
// Promotion ankommen und dann ältere Zeitstempel tragen als der letzte Build.

export type Aenderung = { sha: string; datum: string; titel: string };

export type AenderungArt = "neu" | "behoben";

/**
 * `text` ist der ganze Titel ohne Präfix. Für das Popup ist er zusätzlich in
 * Stichpunkt und Beschreibung zerlegt (User 28.09.2026: „stichpunktartig und je
 * Punkt die Beschreibung, wenn nötig"): `punkt` ist die Kernaussage, `details`
 * sind die weiteren Teile des Titels, `bereich` kommt aus dem Commit-Scope.
 */
export type AenderungKurz = {
  /** Commit-Kennung — daran hängt „gelesen" (wasIstNeuStand.ts). */
  sha: string;
  art: AenderungArt;
  text: string;
  datum: string;
  punkt: string;
  details: string[];
  bereich: string | null;
};

/** Nur was Anwender merken; chore/refactor/docs/test bleiben weg. */
const ART_NACH_PREFIX: Record<string, AenderungArt> = {
  feat: "neu",
  perf: "neu",
  fix: "behoben",
  security: "behoben",
};

export const MAX_EINTRAEGE = 25;

/**
 * PR-Nummern, die bewusst NICHT in „Was ist neu?" stehen (User 07.10.2026). Der
 * Titel eines gemergten Squash-Commits lässt sich nicht mehr ändern, deshalb
 * hängt das Ausblenden an der PR-Nummer am Titelende.
 */
const NICHT_ANZEIGEN = new Set<number>([
  2154, // Schadenlisten in Office für alle Nutzer
  2368, // Bugfix „gezogene Mail sichtbar“ — betraf nur DEV, auf PROD nie aufgetreten (User 02.11.2026)
  2376, // Nachgang zu #2375 (Rubrik → Posteingang) — die Rubrik gab es auf PROD nie
]);

/**
 * Titel, die nach dem Merge nicht mehr stimmen — Text ohne Präfix und PR-Nummer.
 * #2375 hieß „als eigene Rubrik", #2376 hat sie noch vor PROD in den Posteingang
 * zurückgeholt (02.11.2026).
 */
const TITEL_ERSATZ = new Map<number, string>([
  [2375, "Termin-Mails im Posteingang zeigen Wann/Wo und lassen sich direkt zusagen, mit Vorbehalt zusagen oder absagen"],
]);

// Freischaltungen ohne eigenen Code (eine Einstellung wird auf PROD
// eingeschaltet) kündigt ein eigener feat-Commit an — „Was ist neu?" liest nur
// Commit-Titel. Beispiel: Spracherkennung im Mandanten eingeschaltet
// (User 03.11.2026), der Code dafür kam schon mit #2359.

/** Commit-Scope → Anzeigename. Unbekannte Scopes: erster Buchstabe groß; „app" u. Ä. ohne Marke. */
const BEREICH_NACH_SCOPE: Record<string, string | null> = {
  vorgaenge: "Vorgänge",
  vorgang: "Vorgänge",
  aenderungen: "Änderungen",
  app: null,
  deps: null,
};

function bereichAus(scope: string | undefined): string | null {
  if (!scope) return null;
  const s = scope.trim().toLowerCase();
  if (s in BEREICH_NACH_SCOPE) return BEREICH_NACH_SCOPE[s];
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : null;
}

const gross = (t: string): string => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
const woerter = (t: string): number => t.split(/\s+/).filter(Boolean).length;

/** Wörter, mit denen ein Nebensatz beginnt — dort ist das Komma keine Aufzählung. */
const NEBENSATZ = /^(?:der|die|das|dem|den|dessen|deren|denen|ob|dass|wenn|weil|als|wie|damit|sodass|falls|sobald|bevor|nachdem|während|wo|womit|wofür|in dem|in der|an dem|an der|auf dem|auf der|mit dem|mit der|für die|für den|für das)\b/;

/**
 * Zerlegt einen Titel an „, " und „ — "/„ – " in Teile, aber nie innerhalb von
 * „Anführungszeichen" (sonst zerfiele „Nachname, Vorname"). Kein eigener Teil
 * wird:
 * - ein Nebensatz („Ein Mail-Anhang, der schon in der Akte liegt, wird …") —
 *   er bleibt samt dem Satzrest nach ihm am vorigen Teil;
 * - ein Teil unter drei Wörtern („Anrufliste, Detail und Suche …") — er bleibt
 *   am nächsten bzw., ganz hinten, am vorigen Teil.
 * Beim Zusammenfügen bleibt der ursprüngliche Trenner stehen.
 */
export function zerlegeTitel(text: string): string[] {
  const roh: Array<{ t: string; sep: string }> = [];
  let tiefe = 0;
  let aktuell = "";
  let sep = "";
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === "\u201E") tiefe++;
    else if ((c === "\u201C" || c === '"') && tiefe > 0) tiefe--;
    if (tiefe === 0) {
      const strich = /^ [\u2014\u2013] /.exec(text.slice(i));
      if ((c === "," && text[i + 1] === " ") || strich) {
        roh.push({ t: aktuell.trim(), sep });
        sep = strich ? strich[0] : ", ";
        aktuell = "";
        i += (strich ? strich[0].length : 2) - 1;
        continue;
      }
    }
    aktuell += c;
  }
  roh.push({ t: aktuell.trim(), sep });
  const teile = roh.filter((r) => r.t);

  const zusammen: string[] = [];
  let offen: { t: string; sep: string } | null = null;
  let nachNebensatz = false;
  for (const r of teile) {
    const istNebensatz = r.sep === ", " && NEBENSATZ.test(r.t);
    if ((offen || zusammen.length) && (istNebensatz || (nachNebensatz && r.sep === ", "))) {
      if (offen) offen = { t: offen.t + r.sep + r.t, sep: offen.sep };
      else zusammen[zusammen.length - 1] += r.sep + r.t;
      nachNebensatz = istNebensatz;
      continue;
    }
    nachNebensatz = false;
    // Ein kurzer Anfang, der durch einen Nebensatz lang wurde, steht für sich.
    if (offen && woerter(offen.t) >= 3) {
      zusammen.push(offen.t);
      offen = null;
    }
    if (!offen && woerter(r.t) < 3) {
      offen = r;
      continue;
    }
    zusammen.push(offen ? offen.t + r.sep + r.t : r.t);
    offen = null;
  }
  if (offen) {
    if (zusammen.length) zusammen[zusammen.length - 1] += offen.sep + offen.t;
    else zusammen.push(offen.t);
  }
  return zusammen;
}

/** „feat(vollmachten): Kundenerstinformation … (#1867)" → { art: "neu", text: "Kundenerstinformation …", … }. */
export function kurzfassung(a: Aenderung): AenderungKurz | null {
  const m = /^(\w+)(?:\(([^)]*)\))?!?:\s*(.+)$/.exec(a.titel.trim());
  if (!m) return null;
  const art = ART_NACH_PREFIX[m[1].toLowerCase()];
  if (!art) return null;
  const pr = /\(#(\d+)\)\s*$/.exec(m[3]);
  if (pr && NICHT_ANZEIGEN.has(Number(pr[1]))) return null;
  const text = (pr && TITEL_ERSATZ.get(Number(pr[1]))) || m[3]
    .replace(/\s*\(#\d+\)\s*$/, "")
    .replace(/\s*\((?:T-\d+(?:,\s*T-\d+)*|Teil \d+)\)\s*$/i, "")
    .trim();
  if (!text) return null;
  const [punkt, ...details] = zerlegeTitel(text).map(gross);
  return {
    sha: a.sha,
    art,
    text: gross(text),
    datum: a.datum,
    punkt: punkt ?? gross(text),
    details,
    bereich: bereichAus(m[2]),
  };
}

function gueltigeAenderungen(ausgeliefert: unknown): Aenderung[] {
  return Array.isArray(ausgeliefert)
    ? (ausgeliefert as unknown[]).filter(
        (x): x is Aenderung =>
          !!x && typeof (x as Aenderung).sha === "string" && typeof (x as Aenderung).titel === "string",
      )
    : [];
}

/**
 * Was die neue Version gegenüber der laufenden mitbringt, als Kurzfassung.
 * Kennt die laufende Version ihre Commits nicht (lokaler Build ohne Liste),
 * zeigen wir die jüngsten Einträge und sagen das dazu (`ungefaehr`).
 */
export function neueAenderungen(
  ausgeliefert: unknown,
  laufendeCommits: string | undefined,
): { eintraege: AenderungKurz[]; weitere: AenderungKurz[]; ungefaehr: boolean; mehr: number } {
  const liste = gueltigeAenderungen(ausgeliefert);
  const bekannt = new Set((laufendeCommits ?? "").split(",").filter(Boolean));
  const ungefaehr = bekannt.size === 0;
  const neu = ungefaehr ? liste.slice(0, 10) : liste.filter((a) => !bekannt.has(a.sha));
  const kurz = neu.map(kurzfassung).filter((x): x is AenderungKurz => !!x);
  // Die über MAX_EINTRAEGE hinaus bleiben mit — das Popup klappt sie auf Wunsch auf.
  return {
    eintraege: kurz.slice(0, MAX_EINTRAEGE),
    weitere: kurz.slice(MAX_EINTRAEGE),
    ungefaehr,
    mehr: Math.max(0, kurz.length - MAX_EINTRAEGE),
  };
}

// ---------------------------------------------------------------------------
// Ungelesene Änderungen auch früherer Releases (User 01.10.2026: „die
// Release-Logs der vorherigen übernehmen, weil die das noch lesen müssen").
// Wer ein Update am Vormittag weggeklickt hat, sieht dessen Änderungen beim
// nächsten Öffnen noch — bis sie einmal im Popup standen.
// ---------------------------------------------------------------------------

/** Wie weit das Popup zurückschaut — auch beim Nachlesen. */
export const FENSTER_TAGE = 14;
/** Ohne gemerkten Stand (erstes Öffnen): nur die letzte Woche. */
export const ERSTES_MAL_TAGE = 7;

/** Was jemand schon gesehen hat: alles vor `seit` und die Commits in `shas`. */
export type GesehenStand = { seit: string; shas: string[] };

export type AenderungsGruppe = {
  /** „neu" für die gerade ausgelieferte Version, sonst das Datum (JJJJ-MM-TT). */
  schluessel: string;
  titel: string;
  eintraege: AenderungKurz[];
};

export type WasIstNeuModus = "ungelesen" | "alle";

const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Lokales Datum als JJJJ-MM-TT (wie `git log --format=%cs`). */
export function tagSchluessel(d: Date): string {
  const zwei = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
}

export function tageZurueck(heute: Date, tage: number): string {
  return tagSchluessel(new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - tage));
}

/** „Heute", „Gestern", sonst „Mo, 29.09.". */
export function tagTitel(datum: string, heute: Date): string {
  if (datum === tagSchluessel(heute)) return "Heute";
  if (datum === tageZurueck(heute, 1)) return "Gestern";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum);
  if (!m) return datum;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${WOCHENTAG[d.getDay()]}, ${m[3]}.${m[2]}.`;
}

/**
 * Was das Popup zeigt, nach Tagen gruppiert, neueste zuerst.
 *
 * - Oben stehen die Einträge der gerade ausgelieferten Version (ausgelieferte
 *   Liste minus laufende) — immer, auch wenn sie schon einmal angezeigt wurden,
 *   denn um sie geht es beim Hinweis „Neue Version verfügbar".
 * - Darunter `ungelesen`: was in den letzten 14 Tagen kam und noch nie im Popup
 *   stand; ohne gemerkten Stand nur die letzten 7 Tage.
 * - `alle` (Link „Frühere Änderungen anzeigen"): die letzten 14 Tage komplett.
 */
export function wasIstNeuAnsicht(
  ausgeliefert: unknown,
  laufendeCommits: string | undefined,
  gesehen: GesehenStand | null,
  heute: Date,
  modus: WasIstNeuModus = "ungelesen",
): { gruppen: AenderungsGruppe[]; nurTechnisches: boolean } {
  const liste = gueltigeAenderungen(ausgeliefert);
  const bekannt = new Set((laufendeCommits ?? "").split(",").filter(Boolean));
  // Kennt die laufende Version ihre Commits nicht (lokaler Build), gibt es
  // keine „neue Version" — dann zählen nur die Tage.
  const neu = bekannt.size === 0 ? [] : liste.filter((a) => !bekannt.has(a.sha));
  const neuKurz = neu.map(kurzfassung).filter((x): x is AenderungKurz => !!x);
  const neuShas = new Set(neu.map((a) => a.sha));

  const fenster = tageZurueck(heute, FENSTER_TAGE);
  let ab = gesehen || modus === "alle" ? fenster : tageZurueck(heute, ERSTES_MAL_TAGE);
  if (modus === "ungelesen" && gesehen && gesehen.seit > ab) ab = gesehen.seit;
  const gelesen = new Set(gesehen?.shas ?? []);

  const nachTag = new Map<string, AenderungKurz[]>();
  for (const a of liste) {
    if (neuShas.has(a.sha)) continue;
    if (!a.datum || a.datum < ab) continue;
    if (modus === "ungelesen" && gelesen.has(a.sha)) continue;
    const k = kurzfassung(a);
    if (!k) continue;
    const tag = nachTag.get(a.datum);
    if (tag) tag.push(k);
    else nachTag.set(a.datum, [k]);
  }

  const gruppen: AenderungsGruppe[] = [];
  if (neuKurz.length) gruppen.push({ schluessel: "neu", titel: "Mit der neuen Version", eintraege: neuKurz });
  for (const datum of Array.from(nachTag.keys()).sort().reverse()) {
    gruppen.push({ schluessel: datum, titel: tagTitel(datum, heute), eintraege: nachTag.get(datum)! });
  }
  return { gruppen, nurTechnisches: neu.length > 0 && neuKurz.length === 0 };
}

export async function ladeAenderungen(): Promise<unknown> {
  const res = await fetch(`/aenderungen.json?t=${Date.now()}`, { cache: "no-store", credentials: "omit" });
  if (!res.ok) return [];
  return res.json();
}
